import { cva } from 'noddi-ui-common';

/**
 * One Tailwind family per status, deliberately brand-neutral: a calendar must read the same
 * in every white-label theme, so no `primary`/`secondary`/`success` token may enter this
 * file. The status lives in the dot, which is why an entry's own label keeps body color.
 */

/**
 * Google-Calendar-shaped entry: status dot, time, label — on the day cell's background.
 * Spacing is deliberately tight: a comfortable cell fits three two-line entries plus the
 * overflow count, and a cell clips rather than scrolls, so slack here eats a sublabel.
 */
export const calendarEntryVariants = cva(
  'flex w-full min-w-0 flex-col overflow-hidden rounded px-1 py-0.5 text-left text-[11px] leading-tight transition-colors',
  {
    variants: {
      status: {
        needsAction: 'hover:bg-amber-50',
        planned: 'hover:bg-blue-50',
        completed: 'hover:bg-emerald-50',
        unableToComplete: 'hover:bg-orange-50',
        cancelled: 'hover:bg-red-50'
      }
    }
  }
);

/** The legend's circle label, and the same chip wherever a status is named in prose. */
export const calendarStatusChipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium',
  {
    variants: {
      status: {
        needsAction: 'border-amber-200 bg-amber-50 text-amber-800',
        planned: 'border-blue-200 bg-blue-50 text-blue-800',
        completed: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        unableToComplete: 'border-orange-200 bg-orange-50 text-orange-800',
        cancelled: 'border-red-200 bg-red-50 text-red-800'
      }
    }
  }
);

/**
 * `needsAction` and `unableToComplete` sit two families apart on purpose: at 8px a dot has
 * only its hue to go on, and amber-500 beside orange-500 read as the same color.
 */
export const calendarStatusDotVariants = cva('size-2 shrink-0 rounded-full', {
  variants: {
    status: {
      needsAction: 'bg-amber-400',
      planned: 'bg-blue-500',
      completed: 'bg-emerald-500',
      unableToComplete: 'bg-orange-600',
      cancelled: 'bg-red-500'
    }
  }
});
